import { Shimmer } from '@components/ui/skeleton';
import { Box } from '@mui/material';

import { PAGE_MAX_WIDTH, PAGE_PX, TOPBAR_HEIGHT } from './layoutMetrics';

/**
 * Placeholder for the whole app shell, shown while auth and the centre/session
 * scope are still resolving — i.e. on a cold load and after every centre or
 * main-centre switch, both of which hard-reload the page.
 *
 * It mirrors the real Topbar's structure and breakpoints (hamburger below lg,
 * nav and scope chips at lg+), so the chrome appears in its final position
 * immediately and only the content shimmers. That replaces the full-screen
 * spinner, which made a scope switch look like the app had restarted.
 */
export const AppShellSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'white', overflowX: 'hidden' }}>
    {/* Topbar */}
    <Box
      sx={(theme) => ({
        height: TOPBAR_HEIGHT,
        borderBottom: `1px solid ${theme.palette.slate[200]}`,
        bgcolor: 'white',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '100%',
          width: '100%',
          maxWidth: PAGE_MAX_WIDTH,
          mx: 'auto',
          px: PAGE_PX,
        }}
      >
        {/* Hamburger — below lg, matching Topbar */}
        <Shimmer height={24} width={24} radius="6px" sx={{ display: { xs: 'block', lg: 'none' } }} />
        <Shimmer height={40} width={{ xs: 44, lg: 56 }} radius="8px" />

        {/* Desktop nav pills — lg+ only */}
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, ml: '8px' }}>
          {[92, 78, 104, 106, 88, 90].map((w, i) => (
            <Shimmer key={i} height={32} width={w} radius="8px" />
          ))}
        </Box>

        <Box sx={{ display: { xs: 'block', lg: 'none' }, flex: 1 }} />

        {/* Right controls: bell, the three scope chips (lg+), avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <Shimmer height={32} width={32} radius="50%" />
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: '10px' }}>
            <Shimmer height={32} width={84} radius="8px" />
            <Shimmer height={32} width={112} radius="8px" />
            <Shimmer height={32} width={100} radius="8px" />
          </Box>
          <Shimmer height={38} width={{ xs: 38, sm: 130 }} radius="9999px" />
        </Box>
      </Box>
    </Box>

    {/* Page body: the global search bar, then generic content blocks */}
    <Box
      component="main"
      sx={{
        flex: 1,
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        mx: 'auto',
        px: PAGE_PX,
        pt: `calc(${TOPBAR_HEIGHT} + 25px)`,
        pb: '25px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <Shimmer height={48} radius="12px" />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', pt: '10px' }}>
        <Shimmer height={{ xs: 20, sm: 22, md: 24 }} width={{ xs: '55%', sm: 220 }} />
        <Shimmer height={14} width={{ xs: '80%', sm: 300 }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: '16px' }}>
        {[0, 1, 2].map((i) => (
          <Shimmer key={i} height={{ xs: 96, md: 108 }} radius="16px" />
        ))}
      </Box>

      <Shimmer height={{ xs: 280, md: 380 }} radius="16px" />
    </Box>
  </Box>
);
